import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { administrationClient, unwrap, createResource } from '@/shared/api-client';
import { apiClient as workspaceClient } from '@/shared/api-client/workspace';
import { AUTH_ME_QUERY_KEY } from '@/modules/auth/AuthContext';
import type { VenueWeekdayHours } from './operatingHours';

export interface Venue {
  id: string;
  name: string;
  countryCode: string;
  language: string;
  currency: string;
  timezone: string;
  slotGranularityMinutes?: number;
  activeWaiverTemplate: ActiveWaiverTemplateAssignment | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActiveWaiverTemplateAssignment {
  templateId: string;
  version: number;
  name: string;
}

export interface VenuePage {
  items: Venue[];
  page: number;
  pageSize: number;
  total: number;
}

/** An enabled CountryPolicy offered in the create picker; selecting it snapshots these locale defaults. */
export interface CountryOption {
  countryCode: string;
  language: string;
  currency: string;
  timezone: string;
}

export interface CountryOptionPage {
  items: CountryOption[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateVenueInput {
  name: string;
  countryCode: string;
  slotGranularityMinutes: number;
  operatingHours: VenueWeekdayHours[];
}

export interface ChangeLocaleInput {
  language: string;
  currency: string;
  timezone: string;
}

export interface WaiverAcknowledgementItem {
  text: string;
  required: boolean;
}

export interface WaiverTemplateVersion {
  version: number;
  bodyHtml: string;
  acknowledgements: WaiverAcknowledgementItem[];
  createdAt: string;
}

export interface WaiverTemplate {
  id: string;
  name: string;
  createdAt: string;
  latestVersion: WaiverTemplateVersion;
  versions: WaiverTemplateVersion[];
}

export interface WaiverTemplateListResponse {
  items: WaiverTemplate[];
}

export interface CreateWaiverTemplateInput {
  name: string;
  bodyHtml: string;
  acknowledgements: WaiverAcknowledgementItem[];
}

export interface CreateWaiverTemplateVersionInput {
  bodyHtml: string;
  acknowledgements: WaiverAcknowledgementItem[];
}

export interface AssignWaiverTemplateInput {
  templateId: string;
  version: number;
}

const countryOptionsKey = ['venues', 'countries'] as const;
const waiverTemplatesKey = ['venues', 'waiver-templates'] as const;

const getVenues = (): Promise<VenuePage> =>
  unwrap(administrationClient.GET('/api/v1/franchise-administration/venues')) as Promise<VenuePage>;

const getCountryOptionsPage = (): Promise<CountryOptionPage> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/venues/countries'),
  ) as Promise<CountryOptionPage>;

const getCountryOptions = async (): Promise<CountryOption[]> =>
  (await getCountryOptionsPage()).items;

export const createVenue = (input: CreateVenueInput): Promise<Venue> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/venues', { body: input }),
  ) as Promise<Venue>;

const updateVenueName = (id: string, name: string): Promise<Venue> =>
  unwrap(
    administrationClient.PATCH('/api/v1/franchise-administration/venues/{id}', {
      params: { path: { id } },
      body: { name },
    }),
  ) as Promise<Venue>;

const changeVenueLocale = (id: string, input: ChangeLocaleInput): Promise<Venue> =>
  unwrap(
    administrationClient.PATCH('/api/v1/franchise-administration/venues/{id}/locale', {
      params: { path: { id } },
      body: input,
    }),
  ) as Promise<Venue>;

const getWaiverTemplates = (): Promise<WaiverTemplateListResponse> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/waiver-templates' as never),
  ) as Promise<WaiverTemplateListResponse>;

const createWaiverTemplate = (input: CreateWaiverTemplateInput): Promise<WaiverTemplate> =>
  unwrap(
    administrationClient.POST(
      '/api/v1/franchise-administration/waiver-templates' as never,
      {
        body: input,
      } as never,
    ),
  ) as Promise<WaiverTemplate>;

const createWaiverTemplateVersion = (
  templateId: string,
  input: CreateWaiverTemplateVersionInput,
): Promise<WaiverTemplate> =>
  unwrap(
    administrationClient.POST(
      '/api/v1/franchise-administration/waiver-templates/{templateId}/versions' as never,
      {
        params: { path: { templateId } },
        body: input,
      } as never,
    ),
  ) as Promise<WaiverTemplate>;

const assignVenueWaiverTemplate = (
  venueId: string,
  input: AssignWaiverTemplateInput,
): Promise<Venue> =>
  unwrap(
    workspaceClient.PATCH(
      '/api/v1/venue-workspace/venues/{id}/waiver-template-assignment' as never,
      {
        params: { path: { id: venueId } },
        body: input,
      } as never,
    ),
  ) as Promise<Venue>;

/** Venues list + create through the shared resource factory (ADR 0012). The
 *  name/locale dual-update mutations stay bespoke (two PATCH endpoints, one
 *  resource) but compose onto the resource's `useInvalidate` rather than
 *  re-deriving the `['venues']` key. */
const venues = createResource({
  key: ['venues'],
  list: getVenues,
  create: createVenue,
});

export const useVenues = venues.useList;

/** Creating a Venue must also bust `['auth','me']` — LandingResolver reads
 *  its `venues` list from that cache (ADR 0015), so without this the
 *  redirect keeps seeing zero venues until the 60s staleTime lapses. */
export function useCreateVenue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createVenue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: venues.keys.all });
      queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
  });
}

export function useCountryOptions(enabled: boolean) {
  return useQuery({ queryKey: countryOptionsKey, queryFn: getCountryOptions, enabled });
}

export function useWaiverTemplates() {
  return useQuery({ queryKey: waiverTemplatesKey, queryFn: getWaiverTemplates });
}

export function useUpdateVenueName() {
  const invalidate = venues.useInvalidate();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateVenueName(id, name),
    onSuccess: invalidate,
  });
}

export function useChangeVenueLocale() {
  const invalidate = venues.useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ChangeLocaleInput }) =>
      changeVenueLocale(id, input),
    onSuccess: invalidate,
  });
}

export function useCreateWaiverTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createWaiverTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waiverTemplatesKey });
    },
  });
}

export function useCreateWaiverTemplateVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      input,
    }: {
      templateId: string;
      input: CreateWaiverTemplateVersionInput;
    }) => createWaiverTemplateVersion(templateId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waiverTemplatesKey });
    },
  });
}

export function useAssignVenueWaiverTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ venueId, input }: { venueId: string; input: AssignWaiverTemplateInput }) =>
      assignVenueWaiverTemplate(venueId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: venues.keys.all });
      queryClient.invalidateQueries({ queryKey: waiverTemplatesKey });
    },
  });
}
